import { FC, ReactNode, useEffect, useMemo } from 'react';
import { captureException } from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Checkbox, Col, Divider, Form, Row, Skeleton, Typography } from 'antd';
import { FileLock } from 'lucide-react';
import {
    TbActivity,
    TbAlertTriangle,
    TbCalendarTime,
    TbHistory,
    TbLock,
    TbRefresh,
    TbShieldLock,
    TbTrash,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import {
    useUpdateWorkspaceDataRetention,
    workspaceDataRetentionQueryKey,
    workspaceDataRetentionQueryOptions,
} from '@/queries';
import {
    DropdownButtonSelect,
    DropdownMenuLabel,
    EmptySection,
    FlexContentRegion,
    LearnMoreFooter,
    PlanTag,
    PlatformAlert,
    SettingsToggleRow,
} from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore, useWorkspaceStore } from '@/stores';
import { DataRetentionCategory, DataRetentionRules, MemberPermissionType, WorkspaceDataRetentionPolicy } from '@/types';

import {
    DATA_RETENTION_CATEGORY_CONFIGURATIONS,
    fromRetentionSelectValue,
    hasBelowBaselineRetention,
    normalizeDataRetentionRules,
    preservesVersionHistoryInvariant,
    retentionOptionsForCategory,
    RetentionSelectValue,
    toRetentionSelectValue,
} from './dataRetentionConfig';

interface DataRetentionFormValues {
    acknowledgeComplianceResponsibility: boolean;
    enabled: boolean;
    legalHold: boolean;
    rules: DataRetentionRules;
}

interface ApiErrorLike {
    response?: {
        data?: {
            code?: string;
            error?: string;
        };
        status?: number;
    };
}

const categoryIcons: Record<DataRetentionCategory, ReactNode> = {
    activityHistory: <TbActivity aria-hidden='true' />,
    auditLogs: <TbShieldLock aria-hidden='true' />,
    trash: <TbTrash aria-hidden='true' />,
    versionHistory: <TbHistory aria-hidden='true' />,
};

const valuesFromPolicy = (policy: WorkspaceDataRetentionPolicy): DataRetentionFormValues => ({
    acknowledgeComplianceResponsibility: false,
    enabled: policy.enabled,
    legalHold: policy.legalHold,
    rules: normalizeDataRetentionRules(policy.rules),
});

const normalizeFormValues = (values?: Partial<DataRetentionFormValues>): DataRetentionFormValues => ({
    acknowledgeComplianceResponsibility: values?.acknowledgeComplianceResponsibility ?? false,
    enabled: values?.enabled ?? false,
    legalHold: values?.legalHold ?? false,
    rules: normalizeDataRetentionRules(values?.rules),
});

const policyComparableValues = (values: DataRetentionFormValues) => ({
    enabled: values.enabled,
    legalHold: values.legalHold,
    rules: values.rules,
});

const getApiError = (error: unknown) => {
    const response = (error as ApiErrorLike)?.response;

    return {
        code: response?.data?.code,
        message: response?.data?.error,
        status: response?.status,
    };
};

const DataRetention: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [form] = Form.useForm<DataRetentionFormValues>();
    const queryClient = useQueryClient();
    const user = useUserStore((state) => state.selectedUser);
    const workspace = useWorkspaceStore((state) => state.selectedWorkspace);
    const workspaceId = workspace?.id;
    const { hasFeature } = useEntitlements();
    const isEnterprise = hasFeature('dataRetention');
    const permission = user?.activeMember?.permission;
    const canManage = permission === MemberPermissionType.OWNER || permission === MemberPermissionType.ADMIN;
    const canLoadPolicy = Boolean(workspaceId && isEnterprise && canManage);
    const policyQuery = useQuery(workspaceDataRetentionQueryOptions(workspaceId, canLoadPolicy));
    const updatePolicy = useUpdateWorkspaceDataRetention(workspaceId);
    const values = Form.useWatch([], { form, preserve: true });
    const watchedValues = useMemo(() => normalizeFormValues(values), [values]);
    const hasBelowBaseline = hasBelowBaselineRetention(watchedValues.rules);
    const hasValidHistoryOrdering = preservesVersionHistoryInvariant(watchedValues.rules);
    const isDirty = useMemo(() => {
        if (!policyQuery.data || !values?.rules) {
            return false;
        }

        return (
            JSON.stringify(policyComparableValues(watchedValues)) !==
            JSON.stringify(policyComparableValues(valuesFromPolicy(policyQuery.data)))
        );
    }, [policyQuery.data, values, watchedValues]);
    const disabled = !canManage || !isEnterprise || policyQuery.isLoading || updatePolicy.isPending;
    const showPolicyEditor =
        isEnterprise && canManage && !policyQuery.isLoading && !policyQuery.isError && Boolean(policyQuery.data);

    useEffect(() => {
        if (policyQuery.data) {
            form.setFieldsValue(valuesFromPolicy(policyQuery.data));
        }
    }, [form, policyQuery.data]);

    const resetForm = () => {
        if (policyQuery.data) {
            form.setFieldsValue(valuesFromPolicy(policyQuery.data));
        }
    };

    const handlePeriodChange = (category: DataRetentionCategory, value?: RetentionSelectValue) => {
        if (value === undefined) {
            return;
        }

        form.setFieldValue(['rules', category], fromRetentionSelectValue(value));
        form.setFieldValue('acknowledgeComplianceResponsibility', false);
    };

    const handleSave = async () => {
        if (!workspaceId || !policyQuery.data || !isDirty || !hasValidHistoryOrdering) {
            return;
        }

        if (watchedValues.enabled && hasBelowBaseline && !watchedValues.acknowledgeComplianceResponsibility) {
            toast.error('Review the compliance acknowledgement before saving');
            return;
        }

        try {
            const saved = await updatePolicy.mutateAsync({
                acknowledgeComplianceResponsibility: watchedValues.acknowledgeComplianceResponsibility,
                enabled: watchedValues.enabled,
                expectedVersion: policyQuery.data.version,
                legalHold: watchedValues.legalHold,
                rules: watchedValues.rules,
            });

            form.setFieldsValue(valuesFromPolicy(saved));
            toast.success('Data Retention Policy Saved', {
                description: saved.legalHold
                    ? 'Legal hold is active, so automated disposal remains suspended.'
                    : saved.enabled
                      ? 'The updated schedule will be evaluated during the next bounded retention run.'
                      : 'Custom retention is disabled. The standard 30-day Trash lifecycle remains active.',
            });
        } catch (error) {
            captureException(error);
            const apiError = getApiError(error);

            if (apiError.code === 'DATA_RETENTION_POLICY_VERSION_CONFLICT' || apiError.status === 409) {
                await queryClient.invalidateQueries({ queryKey: workspaceDataRetentionQueryKey(workspaceId) });
                toast.error('Data Retention Policy Changed', {
                    description: 'The latest settings were reloaded. Review them before saving again.',
                });
                return;
            }

            toast.error('Could Not Save Data Retention Policy', {
                description: apiError.message ?? 'Review the policy and try again.',
            });
        }
    };

    const lastEnforcementDescription = useMemo(() => {
        const summary = policyQuery.data?.lastEnforcementSummary;

        if (!summary) {
            return 'This policy has not completed a scheduled enforcement run yet.';
        }

        const removedCount = Object.values(summary).reduce((total, count) => total + count, 0);
        const enforcedAt = policyQuery.data?.lastEnforcedAt
            ? new Date(policyQuery.data.lastEnforcedAt).toLocaleString()
            : 'the last scheduled run';

        return `${removedCount.toLocaleString()} expired ${removedCount === 1 ? 'record was' : 'records were'} removed during ${enforcedAt}.`;
    }, [policyQuery.data]);

    if (!workspaceId) {
        return (
            <EmptySection
                description='Select a workspace before configuring its retention policy.'
                height='18rem'
                icon={<FileLock size='1rem' />}
                label='Workspace Required'
            />
        );
    }

    return (
        <section
            aria-labelledby='data-retention-heading'
            data-settings-section='data-retention'
            style={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                margin: '0 auto',
                maxWidth: '68rem',
                minHeight: 0,
                width: '100%',
            }}
        >
            <div data-data-retention-header='true' style={{ flex: 'none' }}>
                <Row align='top' justify='space-between' wrap>
                    <Col flex='auto'>
                        <Typography.Title id='data-retention-heading' level={4} style={{ marginBottom: '0.25rem' }}>
                            <FileLock
                                aria-hidden='true'
                                size='1.125rem'
                                style={{ marginRight: '0.5rem', verticalAlign: '-0.175rem' }}
                            />
                            Data Retention
                            <PlanTag plan='enterprise' style={{ marginLeft: '0.5rem', verticalAlign: '0.125rem' }} />
                        </Typography.Title>
                        <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: 0 }}>
                            Define how long workspace records remain available before scheduled disposal. Your
                            organization remains responsible for mapping these controls to its legal, regulatory,
                            contractual, and litigation-hold obligations.
                        </Typography.Paragraph>
                    </Col>
                </Row>

                <Divider style={{ margin: '1rem 0 0' }} />
            </div>

            <Form<DataRetentionFormValues>
                form={form}
                layout='vertical'
                name='workspace-data-retention'
                style={{ display: 'flex', flex: '1 1 0%', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
                onFinish={handleSave}
            >
                <FlexContentRegion data-data-retention-scroll-region='true' overflowX='hidden' overflowY='auto'>
                    <div style={{ padding: '1rem 0' }}>
                        {!isEnterprise ? (
                            <PlatformAlert
                                action={<PlanTag plan='enterprise' />}
                                description='Upgrade to define category-specific retention windows, suspend disposal with legal hold, and review enforcement status.'
                                status='info'
                                title='Data Retention Requires an Enterprise Plan'
                            />
                        ) : !canManage ? (
                            <PlatformAlert
                                description='Only workspace Owners and Administrators can view or change this security policy.'
                                status='warning'
                                title='Owner or Administrator Access Required'
                            />
                        ) : policyQuery.isLoading ? (
                            <Skeleton active paragraph={{ rows: 8 }} title={{ width: '40%' }} />
                        ) : policyQuery.isError ? (
                            <EmptySection
                                description='The current policy could not be loaded. No retention settings were changed.'
                                extra={
                                    <Button icon={<TbRefresh />} onClick={() => policyQuery.refetch()}>
                                        Try Again
                                    </Button>
                                }
                                height='18rem'
                                icon={<FileLock size='1rem' />}
                                label='Could Not Load Data Retention'
                            />
                        ) : (
                            <>
                                <Typography.Title level={5} style={{ margin: '0 0 0.25rem' }}>
                                    Retention Controls
                                </Typography.Title>
                                <Typography.Paragraph
                                    style={{ color: token.colorTextTertiary, marginBottom: '0.75rem' }}
                                >
                                    Control automated disposal and pause scheduled deletion when legal preservation
                                    obligations apply.
                                </Typography.Paragraph>

                                <div
                                    style={{
                                        background: token.colorBgContainer,
                                        border: `1px solid ${token.colorBorderSecondary}`,
                                        borderRadius: token.borderRadiusLG,
                                        padding: '1rem',
                                    }}
                                >
                                    <SettingsToggleRow
                                        checked={watchedValues.enabled}
                                        description='Automatically apply the retention periods below each night. When turned off, items in Trash are still permanently deleted after 30 days, but the other retention rules are not enforced.'
                                        disabled={disabled}
                                        icon={<TbCalendarTime aria-hidden='true' />}
                                        label='Automated Retention'
                                        loading={updatePolicy.isPending}
                                        switchAriaLabel='Enable automated data retention'
                                        onChange={(checked) => form.setFieldValue('enabled', checked)}
                                    />
                                    <Divider style={{ margin: '0.75rem 0' }} />
                                    <SettingsToggleRow
                                        checked={watchedValues.legalHold}
                                        description='Suspend every automated disposal category while preservation obligations are active.'
                                        disabled={disabled}
                                        icon={<TbLock aria-hidden='true' />}
                                        label='Legal Hold'
                                        loading={updatePolicy.isPending}
                                        switchAriaLabel='Suspend automated disposal with legal hold'
                                        onChange={(checked) => form.setFieldValue('legalHold', checked)}
                                    />
                                </div>

                                {watchedValues.legalHold ? (
                                    <PlatformAlert
                                        description='Scheduled deletion is suspended across all categories. Existing records remain preserved until an authorized Owner or Administrator releases the hold and saves the policy.'
                                        status='warning'
                                        style={{ marginTop: '1rem' }}
                                        title='Legal Hold Is Active'
                                    />
                                ) : null}

                                <Divider style={{ margin: '1rem 0' }} />
                                <Typography.Title level={5} style={{ margin: '0 0 0.25rem' }}>
                                    Retention Schedule
                                </Typography.Title>
                                <Typography.Paragraph
                                    style={{ color: token.colorTextTertiary, marginBottom: '0.75rem' }}
                                >
                                    Disposal is workspace-scoped and processed in limited batches to protect application
                                    and database performance. Retain indefinitely disables automatic deletion for that
                                    category.
                                </Typography.Paragraph>

                                <div
                                    style={{
                                        border: `1px solid ${token.colorBorderSecondary}`,
                                        borderRadius: token.borderRadiusLG,
                                        overflow: 'hidden',
                                    }}
                                >
                                    {DATA_RETENTION_CATEGORY_CONFIGURATIONS.map((configuration, index) => {
                                        const options = retentionOptionsForCategory(configuration).map((option) => ({
                                            description: option.description,
                                            icon: categoryIcons[configuration.category],
                                            label: option.label,
                                            value: option.value,
                                        }));

                                        return (
                                            <Row
                                                key={configuration.category}
                                                align='middle'
                                                style={{
                                                    borderTop:
                                                        index === 0
                                                            ? undefined
                                                            : `1px solid ${token.colorBorderSecondary}`,
                                                    columnGap: '1rem',
                                                    padding: '0.875rem 1rem',
                                                }}
                                                wrap={false}
                                            >
                                                <Col flex='1 1 0' style={{ minWidth: 0 }}>
                                                    <div
                                                        style={{
                                                            alignItems: 'flex-start',
                                                            display: 'flex',
                                                            gap: '0.75rem',
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <Avatar
                                                            aria-hidden='true'
                                                            icon={categoryIcons[configuration.category]}
                                                            shape='square'
                                                            size={28}
                                                            style={{
                                                                background: token.colorFillQuaternary,
                                                                border: `1px solid ${token.colorBorderSecondary}`,
                                                                color: token.colorTextSecondary,
                                                                flex: 'none',
                                                            }}
                                                        />
                                                        <div style={{ minWidth: 0 }}>
                                                            <Typography.Text strong>
                                                                {configuration.label}
                                                            </Typography.Text>
                                                            <Typography.Text
                                                                style={{
                                                                    color: token.colorTextTertiary,
                                                                    display: 'block',
                                                                    marginTop: '0.125rem',
                                                                }}
                                                            >
                                                                {configuration.description}
                                                            </Typography.Text>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col
                                                    flex='0 0 12rem'
                                                    style={{ marginLeft: 'auto', maxWidth: '12rem', minWidth: '12rem' }}
                                                >
                                                    <DropdownButtonSelect<RetentionSelectValue>
                                                        aria-label={`Retention period for ${configuration.label}`}
                                                        disabled={disabled}
                                                        footer={
                                                            <LearnMoreFooter
                                                                topic={configuration.learnMoreTopic}
                                                                variant='dropdown'
                                                            />
                                                        }
                                                        header={
                                                            <DropdownMenuLabel
                                                                icon={categoryIcons[configuration.category]}
                                                            >
                                                                {configuration.dropdownLabel}
                                                            </DropdownMenuLabel>
                                                        }
                                                        menuMinWidth='18rem'
                                                        options={options}
                                                        placement='bottomRight'
                                                        searchPlaceholder={`Search ${configuration.label} Periods...`}
                                                        searchable
                                                        value={toRetentionSelectValue(
                                                            watchedValues.rules[configuration.category],
                                                        )}
                                                        onChange={(value) =>
                                                            handlePeriodChange(configuration.category, value)
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                        );
                                    })}
                                </div>

                                {!hasValidHistoryOrdering ? (
                                    <PlatformAlert
                                        description='Version records depend on their parent activity records. Increase Activity History or reduce Version History before saving.'
                                        status='error'
                                        style={{ marginTop: '1rem' }}
                                        title='Activity History Must Outlast Version History'
                                    />
                                ) : null}

                                {watchedValues.enabled && hasBelowBaseline ? (
                                    <PlatformAlert
                                        description='One or more record categories are below commonly used investment-recordkeeping baselines. Confirm the schedule with your compliance and legal teams; this product control does not determine your obligations.'
                                        icon={<TbAlertTriangle />}
                                        status='warning'
                                        style={{ marginTop: '1rem' }}
                                        title='Short Retention Period Selected'
                                        footer={
                                            <Checkbox
                                                checked={watchedValues.acknowledgeComplianceResponsibility}
                                                disabled={disabled}
                                                onChange={(event) =>
                                                    form.setFieldValue(
                                                        'acknowledgeComplianceResponsibility',
                                                        event.target.checked,
                                                    )
                                                }
                                            >
                                                I understand this setting may permanently remove regulated records and
                                                have verified the schedule with my organization’s authorized compliance
                                                owner.
                                            </Checkbox>
                                        }
                                    />
                                ) : null}
                            </>
                        )}
                    </div>
                </FlexContentRegion>

                {showPolicyEditor ? (
                    <div data-data-retention-actions='true' style={{ flex: 'none' }}>
                        <Divider style={{ margin: '0 0 1rem' }} />
                        <PlatformAlert
                            description={lastEnforcementDescription}
                            status='default'
                            title='Last Enforcement Run'
                        />

                        <Row align='middle' justify='end' style={{ gap: '0.5rem', marginTop: '1rem' }} wrap>
                            <Col>
                                <Button disabled={!isDirty || updatePolicy.isPending} onClick={resetForm}>
                                    Reset
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    disabled={
                                        disabled ||
                                        !isDirty ||
                                        !hasValidHistoryOrdering ||
                                        (watchedValues.enabled &&
                                            hasBelowBaseline &&
                                            !watchedValues.acknowledgeComplianceResponsibility)
                                    }
                                    htmlType='submit'
                                    loading={updatePolicy.isPending}
                                    type='primary'
                                >
                                    Save Retention Policy
                                </Button>
                            </Col>
                        </Row>
                    </div>
                ) : null}

                <footer aria-label='Data retention documentation' style={{ flex: 'none', paddingBottom: '1rem' }}>
                    <Divider style={{ margin: showPolicyEditor ? '1rem 0' : '0 0 1rem' }} />
                    <LearnMoreFooter align='center' topic='workspace data retention and legal holds' />
                </footer>
            </Form>
        </section>
    );
};

export default DataRetention;
